import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import type { ClaimBasics } from "../components/ClaimBasicsSection";
import { Step } from "../workflow/steps";
import type { LineItem, Estimate, MatchPair, GroupBucket, Evidence } from "../types/items";
import { diffServer, buildPairsFromServer } from "../services/diffService";
import { apiUrl } from "../lib/apiBase";
import { claimSession } from "../lib/claimSession";
import { normalizeRoomsFromLevels } from "../utils/normalizeRooms";

export type { LineItem, Estimate, MatchPair, GroupBucket, Evidence } from "../types/items";

export type Side = "carrier" | "insured";

export type RoomRef = {
  key: string;
  name: string;
  level?: string | null;
  itemCount?: number;
  subtotal?: number;
};

export type RoomMapping = {
  insuredToCarrier: Record<string, string[]>;
  confidence: Record<string, number>;
  source: "manual" | "heuristic" | "ai";
  updatedAt: number;
};

type WorkflowState = {
  step: Step;
  setStep: (s: Step) => void;

  left?: Estimate;
  right?: Estimate;
  setLeft: (estimate: Estimate | undefined) => void;
  setRight: (estimate: Estimate | undefined) => void;
  claimBasics: ClaimBasics;
  setClaimBasics: Dispatch<SetStateAction<ClaimBasics>>;
  carrierOnLeft: boolean;
  setCarrierOnLeft: Dispatch<SetStateAction<boolean>>;

  roomMapping: RoomMapping | null;
  setRoomMapping: Dispatch<SetStateAction<RoomMapping | null>>;

  pairs: MatchPair[];
  setPairs: Dispatch<SetStateAction<MatchPair[]>>;

  groups: GroupBucket[];
  setGroups: Dispatch<SetStateAction<GroupBucket[]>>;

  pairEvidence: Record<number, Evidence>;
  addPairPhotos: (index: number, urls: string[]) => void;
  removePairPhoto: (index: number, url: string) => void;
  addPairCode: (index: number, code: string) => void;
  removePairCode: (index: number, codeIndex: number) => void;
  setPairPhotoCaption: (index: number, photoIndex: number, caption: string) => void;

  addAllMissingAndRediff: () => Promise<void>;
  getPairsWithEvidence: () => MatchPair[];

  wizardData: WizardPayload | null;
  wizardLoading: boolean;
  wizardError: string | null;
};

const WorkflowContext = createContext<WorkflowState | null>(null);

type WizardRoom = {
  name: string;
  estimatedItemCount?: number;
  recapPercent?: number;
};

type WizardCategory = {
  name: string;
  recapAmount: number | null;
  recapTax: number | null;
  recapNet: number | null;
};

type WizardLineItem = {
  description: string;
  qty: number | null;
  unit: string | null;
  total: number | null;
  projectCategory: string | null;
  room_name: string | null;
  code: string | null;
  flags: string[];
};

function normalizeWizardLineItems(items?: WizardLineItem[]): LineItem[] {
  if (!Array.isArray(items)) return [];
  return items.map((item) => ({
    ...(item as LineItem),
    code: item.code ?? undefined,
    description: String(item.description ?? "").trim(),
    qty: Number(item.qty ?? 0),
    unit: item.unit ?? undefined,
    total: item.total ?? undefined,
    projectCategory: item.projectCategory ?? undefined,
    room_name: item.room_name ?? undefined,
  }));
}

function buildRoomsFromLineItems(items: LineItem[]) {
  const parseLineNoFromId = (value: unknown) => {
    if (value === null || value === undefined) return undefined;
    const id = String(value).trim();
    if (!id) return undefined;
    if (/^\d+$/.test(id)) return Number(id);
    const tailMatch = id.match(/(?:-|_)(\d+)\s*$/);
    if (!tailMatch) return undefined;
    const num = Number(tailMatch[1]);
    return Number.isFinite(num) ? num : undefined;
  };

  const pickLineNo = (item: LineItem) => {
    const candidates = [
      (item as any)?.estimateLineNo,
      (item as any)?.lineNo,
      (item as any)?.line_no,
      (item as any)?.lineNumber,
      (item as any)?.line_number,
      (item as any)?.estimateLineNumber,
      (item as any)?.estimate_line_no,
    ];
    for (const value of candidates) {
      if (value === null || value === undefined || value === "") continue;
      const num = Number(value);
      if (Number.isFinite(num)) return num;
    }
    const idLineNo = parseLineNoFromId((item as any)?.id);
    if (Number.isFinite(idLineNo)) return idLineNo;
    return undefined;
  };

  const orderItemsByLineNo = (roomItems: LineItem[]) => {
    if (!Array.isArray(roomItems) || roomItems.length === 0) return [];
    const entries = roomItems.map((item, index) => ({
      item,
      index,
      lineNo: pickLineNo(item),
    }));
    entries.sort((a, b) => {
      const aHas = Number.isFinite(a.lineNo);
      const bHas = Number.isFinite(b.lineNo);
      if (aHas && bHas) return (a.lineNo as number) - (b.lineNo as number) || a.index - b.index;
      if (aHas) return -1;
      if (bHas) return 1;
      return a.index - b.index;
    });
    return entries.map((entry) => {
      if (Number.isFinite(entry.lineNo)) {
        const lineNo = entry.lineNo as number;
        if ((entry.item as any)?.estimateLineNo === lineNo) return entry.item;
        return { ...entry.item, estimateLineNo: lineNo };
      }
      return entry.item;
    });
  };

  const getRoomMinLineNo = (roomItems: LineItem[]) => {
    let min = Number.POSITIVE_INFINITY;
    for (const item of roomItems || []) {
      const lineNo = pickLineNo(item);
      if (Number.isFinite(lineNo)) min = Math.min(min, lineNo as number);
    }
    return min;
  };

  const map = new Map<
    string,
    { name: string; level?: string | null; items: LineItem[] }
  >();

  for (const item of items) {
    const name = String(
      (item as any).room_name ?? item.room ?? item.projectCategory ?? "Unassigned",
    ).trim();
    const level = (item as any).level != null ? String((item as any).level).trim() : null;
    const key = `${level || ""}::${name || "Unassigned"}`.trim();

    const existing = map.get(key);
    if (existing) {
      existing.items.push(item);
    } else {
      map.set(key, {
        name: name || "Unassigned",
        level: level || undefined,
        items: [item],
      });
    }
  }

  return Array.from(map.values())
    .map((room, index) => {
      const orderedItems = orderItemsByLineNo(room.items);
      return {
        room: { ...room, items: orderedItems },
        index,
        minLineNo: getRoomMinLineNo(orderedItems),
      };
    })
    .sort((a, b) => {
      const aHas = Number.isFinite(a.minLineNo);
      const bHas = Number.isFinite(b.minLineNo);
      if (aHas && bHas) return a.minLineNo - b.minLineNo || a.index - b.index;
      if (aHas) return -1;
      if (bHas) return 1;
      return a.index - b.index;
    })
    .map((entry) => entry.room);
}

type WizardSide = {
  uiRooms: WizardRoom[];
  uiCategories: WizardCategory[];
  uiLineItems: WizardLineItem[];
  claimInfo?: Record<string, string>;
  levels?: any[];
  rooms?: any[];
  flatItems?: any[];
};

type WizardPayload = {
  carrier: WizardSide;
  insured: WizardSide;
  carrierRooms: NormalizedRoom[];
  insuredRooms: NormalizedRoom[];
};

export type NormalizedRoom = ReturnType<typeof normalizeRoomsFromLevels>[number];

export const initialClaimBasics: ClaimBasics = {
  claimId: "",
  policyNumber: "",
  policyholderName: "",
  propertyAddress: "",
  carrierName: "",
  dateOfLoss: "",
  typeOfLoss: "",
  deductible: "",
  enableOcr: true,
  useAiParse: false,
};

function normalizeClaimBasics(raw: unknown): ClaimBasics {
  if (!raw || typeof raw !== "object") return initialClaimBasics;
  const data = raw as Partial<ClaimBasics>;
  return {
    ...initialClaimBasics,
    claimId: typeof data.claimId === "string" ? data.claimId : "",
    policyNumber: typeof data.policyNumber === "string" ? data.policyNumber : "",
    policyholderName: typeof data.policyholderName === "string" ? data.policyholderName : "",
    propertyAddress: typeof data.propertyAddress === "string" ? data.propertyAddress : "",
    carrierName: typeof data.carrierName === "string" ? data.carrierName : "",
    dateOfLoss: typeof data.dateOfLoss === "string" ? data.dateOfLoss : "",
    typeOfLoss: typeof data.typeOfLoss === "string" ? data.typeOfLoss : "",
    deductible: typeof data.deductible === "string" ? data.deductible : "",
    enableOcr: typeof data.enableOcr === "boolean" ? data.enableOcr : initialClaimBasics.enableOcr,
    useAiParse: typeof data.useAiParse === "boolean" ? data.useAiParse : initialClaimBasics.useAiParse,
  };
}

function normalizeMappingRecord(record: Record<string, unknown> | undefined): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  if (!record || typeof record !== "object") return out;
  Object.entries(record).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      out[key] = value.map((entry) => String(entry).trim()).filter(Boolean);
    } else if (typeof value === "string") {
      const trimmed = value.trim();
      out[key] = trimmed ? [trimmed] : [];
    } else {
      out[key] = [];
    }
  });
  return out;
}

function coerceRoomMapping(raw: unknown): RoomMapping | null {
  if (!raw || typeof raw !== "object") return null;
  const data = raw as Record<string, unknown>;
  if ("insuredToCarrier" in data) {
    const mapping = normalizeMappingRecord(data.insuredToCarrier as Record<string, unknown>);
    const confidence =
      data.confidence && typeof data.confidence === "object"
        ? (data.confidence as Record<string, number>)
        : {};
    const source = data.source === "ai" || data.source === "heuristic" || data.source === "manual" ? data.source : "manual";
    const updatedAt = typeof data.updatedAt === "number" ? data.updatedAt : Date.now();
    return {
      insuredToCarrier: mapping,
      confidence,
      source,
      updatedAt,
    };
  }
  return {
    insuredToCarrier: normalizeMappingRecord(data),
    confidence: {},
    source: "manual",
    updatedAt: Date.now(),
  };
}

function cleanupEntry(entry: Evidence | undefined): Evidence | undefined {
  if (!entry) return undefined;
  const photos = entry.photos?.filter(Boolean);
  const codes = entry.codes?.filter(Boolean);
  const captions = entry.captions?.filter((_, idx) => !!photos?.[idx]);
  if (!photos?.length && !codes?.length) return undefined;
  return {
    ...(photos?.length ? { photos } : {}),
    ...(codes?.length ? { codes } : {}),
    ...(captions?.length ? { captions } : {}),
  };
}

export const WorkflowProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [step, setStep] = useState<Step>(Step.UploadReview);
  const [left, setLeft] = useState<Estimate>();
  const [right, setRight] = useState<Estimate>();
  const [claimBasics, setClaimBasics] = useState<ClaimBasics>(() =>
    normalizeClaimBasics(claimSession.getClaimBasics()),
  );
  const [carrierOnLeft, setCarrierOnLeft] = useState<boolean>(() =>
    claimSession.getCarrierOnLeft(),
  );
  const [roomMapping, setRoomMapping] = useState<RoomMapping | null>(null);
  const [pairs, setPairs] = useState<MatchPair[]>([]);
  const [groups, setGroups] = useState<GroupBucket[]>([]);
  const [pairEvidence, setPairEvidence] = useState<Record<number, Evidence>>({});
  const [wizardData, setWizardData] = useState<WizardPayload | null>(null);
  const [wizardLoading, setWizardLoading] = useState(false);
  const [wizardError, setWizardError] = useState<string | null>(null);

  const roomMapStorageKey = useMemo(() => {
    const claimId = right?.claimInfo?.claimNumber || left?.claimInfo?.claimNumber || "claim";
    const carrierId = left?.sessionId || "";
    const insuredId = right?.sessionId || "";
    return `room_map:${claimId}:${carrierId}:${insuredId}`;
  }, [left?.sessionId, right?.sessionId, left?.claimInfo, right?.claimInfo]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(roomMapStorageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      const normalized = coerceRoomMapping(parsed);
      if (normalized) setRoomMapping(normalized);
    } catch {
      /* ignore */
    }
  }, [roomMapStorageKey]);

  useEffect(() => {
    try {
      if (!roomMapping) return;
      localStorage.setItem(roomMapStorageKey, JSON.stringify(roomMapping));
    } catch {
      /* ignore */
    }
  }, [roomMapStorageKey, roomMapping]);

  useEffect(() => {
    claimSession.setClaimBasics(claimBasics);
  }, [claimBasics]);
  useEffect(() => {
    if (!claimBasics.enableOcr) {
      setClaimBasics((prev) => ({ ...prev, enableOcr: true }));
    }
  }, [claimBasics.enableOcr, setClaimBasics]);
  useEffect(() => {
    claimSession.setCarrierOnLeft(carrierOnLeft);
  }, [carrierOnLeft]);

  const addPairPhotos = useCallback((index: number, urls: string[]) => {
    if (!urls.length) return;
    setPairEvidence((prev) => {
      const current = prev[index] || {};
      const photos = [...(current.photos || []), ...urls];
      const captions = [...(current.captions || []), ...urls.map(() => "")];
      return { ...prev, [index]: { ...current, photos, captions } };
    });
  }, []);

  const removePairPhoto = useCallback((index: number, url: string) => {
    setPairEvidence((prev) => {
      const current = prev[index];
      if (!current) return prev;
      const photos = current.photos || [];
      const idx = photos.indexOf(url);
      if (idx === -1) return prev;
      const remainingPhotos = photos.filter((_, i) => i !== idx);
      const remainingCaptions = (current.captions || []).filter((_, i) => i !== idx);
      const nextEntry = cleanupEntry({ ...current, photos: remainingPhotos, captions: remainingCaptions });
      if (!nextEntry) {
        const { [index]: _omit, ...rest } = prev;
        return rest;
      }
      return { ...prev, [index]: nextEntry };
    });
  }, []);

  const addPairCode = useCallback((index: number, code: string) => {
    const trimmed = code.trim();
    if (!trimmed) return;
    setPairEvidence((prev) => {
      const current = prev[index] || {};
      const codes = [...(current.codes || []), trimmed];
      return { ...prev, [index]: { ...current, codes } };
    });
  }, []);

  const removePairCode = useCallback((index: number, codeIndex: number) => {
    setPairEvidence((prev) => {
      const current = prev[index];
      if (!current) return prev;
      const remaining = (current.codes || []).filter((_, i) => i !== codeIndex);
      const nextEntry = cleanupEntry({ ...current, codes: remaining });
      if (!nextEntry) {
        const { [index]: _omit, ...rest } = prev;
        return rest;
      }
      return { ...prev, [index]: nextEntry };
    });
  }, []);

  const setPairPhotoCaption = useCallback((index: number, photoIndex: number, caption: string) => {
    setPairEvidence((prev) => {
      const current = prev[index] || {};
      const caps = [...(current.captions || [])];
      const needed = (current.photos?.length || 0) - caps.length;
      if (needed > 0) caps.push(...Array(needed).fill(""));
      caps[photoIndex] = caption;
      return { ...prev, [index]: { ...current, captions: caps } };
    });
  }, []);

  const addAllMissingAndRediff = useCallback(async () => {
    if (!left || !right) return;
    const missing = pairs.filter((pair) => pair.status === "missing" && pair.left);
    if (!missing.length) return;

    const merged: LineItem[] = [
      ...(right.flatItems || []),
      ...missing.map((pair) => ({
        code: pair.left?.code,
        description: pair.left?.description || "",
        qty: pair.left?.qty || 0,
        unit: pair.left?.unit,
        unit_price: pair.left?.unit_price,
        total: pair.left?.total,
        category: pair.left?.category,
        room: pair.left?.room,
      })),
    ];

    const updatedRight: Estimate = { ...right, flatItems: merged };
    setRight(updatedRight);

    const carrierItems = (left.flatItems || []) as LineItem[];
    const insuredItems = merged;
    try {
      const diff = await diffServer(carrierItems, insuredItems);
      const nextPairs = buildPairsFromServer(carrierItems, insuredItems, diff);
      setPairs(nextPairs);
    } catch (error) {
      console.warn("Re-diff failed after adding missing items", error);
    }
  }, [left, right, pairs]);

  const getPairsWithEvidence = useCallback(
    () => pairs.map((pair, index) => (pairEvidence[index] ? { ...pair, evidence: pairEvidence[index] } : pair)),
    [pairs, pairEvidence],
  );

  useEffect(() => {
    let cancelled = false;
    async function loadWizardData() {
      if (!left?.parsedRaw || !right?.parsedRaw) {
        if (!cancelled) {
          setWizardData(null);
          setWizardError(null);
          setWizardLoading(false);
        }
        return;
      }
      setWizardLoading(true);
      setWizardError(null);
      try {
        const res = await fetch(apiUrl("/wizard/normalize"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ parsedCarrier: left.parsedRaw, parsedInsured: right.parsedRaw }),
        });
        if (!res.ok) {
          throw new Error(`wizard normalize failed (${res.status})`);
        }
        const json = await res.json();
        if (cancelled) return;
        if (json?.ok) {
          const ensureSide = (side: Partial<WizardSide> | undefined): WizardSide => ({
            uiRooms: Array.isArray(side?.uiRooms) ? side.uiRooms : [],
            uiCategories: Array.isArray(side?.uiCategories) ? side.uiCategories : [],
            uiLineItems: Array.isArray(side?.uiLineItems) ? side.uiLineItems : [],
            claimInfo:
              side?.claimInfo && typeof side.claimInfo === "object" ? (side.claimInfo as Record<string, string>) : {},
            levels: Array.isArray(side?.levels) ? side.levels : [],
            rooms: Array.isArray(side?.rooms) ? side.rooms : [],
            flatItems: Array.isArray(side?.flatItems) ? side.flatItems : [],
          });

          const carrierRooms = normalizeRoomsFromLevels(json.carrier?.levels || []);
          const insuredRooms = normalizeRoomsFromLevels(json.insured?.levels || []);

          setWizardData({
            carrier: ensureSide(json.carrier),
            insured: ensureSide(json.insured),
            carrierRooms,
            insuredRooms,
          });
        } else {
          throw new Error(json?.error || "wizard normalize failed");
        }
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : "Failed to normalize wizard data";
        setWizardError(message);
        setWizardData(null);
      } finally {
        if (!cancelled) {
          setWizardLoading(false);
        }
      }
    }
    loadWizardData();
    return () => {
      cancelled = true;
    };
  }, [left?.parsedRaw, right?.parsedRaw]);

  useEffect(() => {
    if (!wizardData) return;

    const carrierItems = normalizeWizardLineItems(wizardData?.carrier?.uiLineItems);
    if (carrierItems.length) {
      setLeft((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          flatItems: carrierItems,
          rooms: buildRoomsFromLineItems(carrierItems),
        };
      });
    }

    const insuredItems = normalizeWizardLineItems(wizardData?.insured?.uiLineItems);
    if (insuredItems.length) {
      setRight((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          flatItems: insuredItems,
          rooms: buildRoomsFromLineItems(insuredItems),
        };
      });
    }
  }, [wizardData, setLeft, setRight]);

  const value = useMemo(
    () => ({
      step,
      setStep,
      left,
      right,
      setLeft,
      setRight,
      claimBasics,
      setClaimBasics,
      carrierOnLeft,
      setCarrierOnLeft,
      roomMapping,
      setRoomMapping,
      pairs,
      setPairs,
      groups,
      setGroups,
      pairEvidence,
      addPairPhotos,
      removePairPhoto,
      addPairCode,
      removePairCode,
      setPairPhotoCaption,
      addAllMissingAndRediff,
      getPairsWithEvidence,
      wizardData,
      wizardLoading,
      wizardError,
    }),
    [
      step,
      left,
      right,
      roomMapping,
      pairs,
      groups,
      pairEvidence,
      claimBasics,
      carrierOnLeft,
      addPairPhotos,
      removePairPhoto,
      addPairCode,
      removePairCode,
      setPairPhotoCaption,
      addAllMissingAndRediff,
      getPairsWithEvidence,
      wizardData,
      wizardLoading,
      wizardError,
    ],
  );

  return <WorkflowContext.Provider value={value}>{children}</WorkflowContext.Provider>;
};

export const useWorkflow = () => {
  const ctx = useContext(WorkflowContext);
  if (!ctx) throw new Error("WorkflowProvider missing");
  return ctx;
};
